import User, {
  UserDriverEditRequestAddress,
  UserDriverEditRequestAddressType,
  UserDriverEditRequestCarOwnership,
  UserDriverEditRequestCitizenship,
  UserDriverEditRequestDriver,
  UserDriverEditRequestFormOfEmployment,
  UserDriverEditRequestLanguage,
  UserDriverEditRequestTaxiDriverAssociation,
  UserDriverEditRequestUser,
} from "../../../../api/raily/user/driver/edit/user-driver-edit.request";
import AddressType from "../../../../types/address-type";
import UserCitizenship from "../../../../types/user-citizenship";
import UserFormOfEmployment from "../../../../types/user-form-of-employment";
import UserLanguage from "../../../../types/user-language";
import UserVehicleOwnership from "../../../../types/user-vehicle-ownership-form";
import UserDriverEditParams, {
  UserDriverEditParamsAddress,
  UserDriverEditParamsDriver,
  UserDriverEditParamsTaxiDriverAssociation,
  UserDriverEditParamsUser,
} from "./user-driver-edit-params";

const createFormOfEmployment = (
  formOfEmployment: UserFormOfEmployment,
): UserDriverEditRequestFormOfEmployment => {
  switch (formOfEmployment) {
    case UserFormOfEmployment.B2B:
      return UserDriverEditRequestFormOfEmployment.B2B;
    case UserFormOfEmployment.EMPLOYMENT:
      return UserDriverEditRequestFormOfEmployment.EMPLOYMENT;
  }
};

const createLanguage = (type: UserLanguage): UserDriverEditRequestLanguage => {
  switch (type) {
    case UserLanguage.BY:
      return UserDriverEditRequestLanguage.BY;
    case UserLanguage.CZ:
      return UserDriverEditRequestLanguage.CZ;
    case UserLanguage.DE:
      return UserDriverEditRequestLanguage.DE;
    case UserLanguage.LT:
      return UserDriverEditRequestLanguage.LT;
    case UserLanguage.PL:
      return UserDriverEditRequestLanguage.PL;
    case UserLanguage.RU:
      return UserDriverEditRequestLanguage.RU;
    case UserLanguage.SK:
      return UserDriverEditRequestLanguage.SK;
    case UserLanguage.UA:
      return UserDriverEditRequestLanguage.UA;
    case UserLanguage.EN:
      return UserDriverEditRequestLanguage.EN;
    case UserLanguage.ES:
      return UserDriverEditRequestLanguage.ES;
    case UserLanguage.FR:
      return UserDriverEditRequestLanguage.FR;
  }
};

const createLanguages = (
  languages: UserLanguage[],
): UserDriverEditRequestLanguage[] => {
  const requestLanguages: UserDriverEditRequestLanguage[] =
    languages.map(createLanguage);

  return requestLanguages;
};

const createCitizenship = (
  type: UserCitizenship,
): UserDriverEditRequestCitizenship => {
  switch (type) {
    case UserCitizenship.BY:
      return UserDriverEditRequestCitizenship.BY;
    case UserCitizenship.CZ:
      return UserDriverEditRequestCitizenship.CZ;
    case UserCitizenship.DE:
      return UserDriverEditRequestCitizenship.DE;
    case UserCitizenship.LT:
      return UserDriverEditRequestCitizenship.LT;
    case UserCitizenship.PL:
      return UserDriverEditRequestCitizenship.PL;
    case UserCitizenship.RU:
      return UserDriverEditRequestCitizenship.RU;
    case UserCitizenship.SK:
      return UserDriverEditRequestCitizenship.SK;
    case UserCitizenship.UA:
      return UserDriverEditRequestCitizenship.UA;
  }
};

const createCarOwnership = (
  type: UserVehicleOwnership,
): UserDriverEditRequestCarOwnership => {
  switch (type) {
    case UserVehicleOwnership.BUSINESS:
      return UserDriverEditRequestCarOwnership.BUSINESS;
    case UserVehicleOwnership.OWNER:
      return UserDriverEditRequestCarOwnership.OWNER;
  }
};

const createAddressType = (
  type: AddressType,
): UserDriverEditRequestAddressType => {
  switch (type) {
    case AddressType.MAILING:
      return UserDriverEditRequestAddressType.MAILING;
    case AddressType.STARTING:
      return UserDriverEditRequestAddressType.STARTING;
  }
};

const createTaxiDriverAssociation = (
  association: UserDriverEditParamsTaxiDriverAssociation,
): UserDriverEditRequestTaxiDriverAssociation => {
  if ("taxId" in association) {
    return {
      apartment: association.apartment,
      companyName: association.companyName,
      contractType: createFormOfEmployment(association.contractType),
      country: association.country,
      description: association.description,
      fleetPartnerId: association.fleetPartnerId,
      houseNo: association.houseNo,
      id: association.id,
      krsNo: association.krsNo,
      regon: association.regon,
      street: association.street,
      taxId: association.taxId,
      town: association.town,
      zipCode: association.zipCode,
    };
  } else {
    return {
      contractType: createFormOfEmployment(association.contractType),
      fleetPartnerId: association.fleetPartnerId,
      id: association.id,
    };
  }
};

const createTaxiDriverAssociations = (
  associations: UserDriverEditParamsTaxiDriverAssociation[],
): UserDriverEditRequestTaxiDriverAssociation[] => {
  return associations.map(createTaxiDriverAssociation);
};

const createUser = (
  user: UserDriverEditParamsUser,
): UserDriverEditRequestUser => {
  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    mobilePrimary: user.mobilePrimary,
    mobilePrimaryPrefix: user.mobilePrimaryPrefix,
    mobileSecondary: user.mobileSecondary,
    mobileSecondaryPrefix: user.mobileSecondaryPrefix,
  };
};

const createAddress = (
  address: UserDriverEditParamsAddress,
): UserDriverEditRequestAddress => {
  return {
    apartment: address.apartment,
    country: address.country,
    description: address.description,
    houseNo: address.houseNo,
    lat: address.lat,
    lon: address.lon,
    street: address.street,
    town: address.town,
    type: createAddressType(address.type),
    zipCode: address.zipCode,
  };
};

const createAddresses = (
  addresses: UserDriverEditParamsAddress[],
): UserDriverEditRequestAddress[] => {
  const requestAddresses: UserDriverEditRequestAddress[] =
    addresses.map(createAddress);

  return requestAddresses;
};

const createDriver = (
  driver: UserDriverEditParamsDriver,
): UserDriverEditRequestDriver => {
  return {
    birthDate: driver.birthDate,
    birthPlace: driver.birthPlace,
    carMake: driver.carMake,
    carModel: driver.carModel,
    carOwnership: createCarOwnership(driver.carOwnership),
    carProdYear: driver.carProdYear,
    carRegNo: driver.carRegNo,
    carSeatsNo: driver.carSeatsNo,
    citizenship: createCitizenship(driver.citizenship),
    experience: driver.experience,
    idNumber: driver.idNumber,
    languages: createLanguages(driver.languages),
  };
};

const create = (params: UserDriverEditParams): User => {
  return {
    driver: createDriver(params.driver),
    addresses: createAddresses(params.addresses),
    associations: createTaxiDriverAssociations(params.associations),
    user: createUser(params.user),
  };
};

const userDriverEditParamsFactory = {
  create,
};

export default userDriverEditParamsFactory;
